import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Statistic,
  Empty,
  Spin,
  Input,
  Tag,
  Progress,
  Space,
  message,
} from "antd";

import {
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileDoneOutlined,
  DollarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import { DatePicker } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getAllExpense } from "../../../../../api/expenseApi";
import { getIngredients } from "../../../../../api/foodstockApi";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);

const { Option } = Select;
const { RangePicker } = DatePicker;

const StockReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all"); // CHANGED: from "today" to "all"
  const [range, setRange] = useState(null);
  const [ingredientStock, setIngredientStock] = useState([]);

  // ============================================
  // PDF GENERATION
  // ============================================
  const handlePDF = () => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [100, 200], // ✅ 4-inch thermal width
  });

  doc.setFontSize(12);
  doc.text("STOCK REPORT", 10, 10);

  doc.setFontSize(8);
  doc.text(`Date: ${dayjs().format("DD MMM YYYY")}`, 10, 16);

  const tableData = rows.map((r) => [
    r.item_name,
   
    `${Number(r.remaining_stock || 0)} ${r.unit}`,
    `Rs.${Number(r.total_amount || 0).toFixed(2)}`,
    r.stock_status,
  ]);

 autoTable(doc, {
  startY: 22,
  head: [["Item", "Remaining", "Amount", "Status"]],
  body: tableData,

  styles: {
    fontSize: 7,
    cellPadding: 2,
    font: "helvetica",   // ✅ IMPORTANT FIX
  },

  headStyles: {
    fillColor: [211, 32, 41],
    textColor: 255,
    fontSize: 8,
    font: "helvetica",
  },

  columnStyles: {
    3: { halign: "right" }, // Amount column right align
  },
});

  doc.save("stock-report.pdf");
};


const handleFullPDF = () => {
  const doc = new jsPDF({
    orientation: "landscape", // better for full table
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(12);
  doc.text("FULL STOCK REPORT", 14, 10);

  doc.setFontSize(8);
  doc.text(`Date: ${dayjs().format("DD MMM YYYY")}`, 14, 16);

  const tableData = rows.map((r) => [
    r.item_name,
    `${Number(r.total_qty || 0)} ${r.unit}`,
    `${Number(r.remaining_stock || 0)} ${r.unit}`,
    `Rs.${Number(r.avg_rate || 0)}`,
    `Rs.${Number(r.total_amount || 0).toFixed(2)}`,
    r.line_count,
    r.stock_status,
    r.vendors?.join(", ") || "-",
    r.last_purchase ? dayjs(r.last_purchase).format("DD MMM YYYY") : "-",
  ]);

  autoTable(doc, {
    startY: 22,
    head: [[
      "Item",
      "Total Qty",
      "Remaining",
      "Avg Rate",
      "Amount",
      "Entries",
      "Status",
      "Vendors",
      "Last Purchase"
    ]],
    body: tableData,
    styles: { fontSize: 6 },
  });

  doc.save("full-stock-report.pdf");
};

  // ============================================
  // FETCH EXPENSES (ONLY FOOD STOCK ITEMS)
  // ============================================
  const fetchExpenses = async (showMessage = false) => {
    try {
      setLoading(true);
      const res = await getAllExpense();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      
      // CHANGED: Robust filtering - handles boolean, number (1/0), and string values
      const foodStockExpenses = list.filter(expense => {
        const val = expense.add_to_food_stock;
        // Check if value is truthy: true, 1, "1", "true"
        return val === true || val === 1 || val === "1" || val === "true";
      });
      
      setExpenses(foodStockExpenses);
      
      const stockRes = await getIngredients();
      setIngredientStock(stockRes.data || []);

      if (showMessage) {
        message.success(`Stock report loaded (${foodStockExpenses.length} food stock expenses)`);
      }
    } catch (err) {
      console.log("STOCK FETCH ERROR:", err);
      message.error("Failed to load stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  // ============================================
  // BUILD STOCK DATA FROM EXPENSE ITEMS (Only Food Stock)
  // ============================================
 const rows = useMemo(() => {

  const grouped = {};

  expenses.forEach((expense) => {

    const isFoodStock =
      expense.add_to_food_stock === true ||
      expense.add_to_food_stock === 1 ||
      expense.add_to_food_stock === "1";

    if (!isFoodStock) return;

    // =========================
    // DATE FILTER CHECK
    // =========================
    let includeExpense = true;

    const expenseDate = dayjs(expense.expense_date);

    if (dateFilter === "today") {

      includeExpense =
        expenseDate.format("YYYY-MM-DD") ===
        dayjs().format("YYYY-MM-DD");
    }

    else if (dateFilter === "yesterday") {

      includeExpense =
        expenseDate.format("YYYY-MM-DD") ===
        dayjs().subtract(1, "day").format("YYYY-MM-DD");
    }

    else if (
      dateFilter === "custom" &&
      range &&
      range.length === 2
    ) {

      includeExpense = expenseDate.isBetween(
        range[0],
        range[1],
        null,
        "[]"
      );
    }

    if (!includeExpense) return;

    // =========================
    // GROUP ITEMS
    // =========================
    (expense.items || []).forEach((item) => {

      const key = item.name?.trim()?.toLowerCase();

      if (!key) return;

      if (!grouped[key]) {

        grouped[key] = {
          key,
          item_name: item.name,
          total_qty: 0,
          total_amount: 0,
          avg_rate: 0,
          unit: item.unit || "-",
          line_count: 0,
          vendors: [],
          last_purchase: expense.expense_date,
        };
      }

      grouped[key].total_qty += Number(item.qty || 0);

      grouped[key].total_amount += Number(item.amount || 0);

      grouped[key].line_count += 1;

      if (
        expense.vendor_name &&
        !grouped[key].vendors.includes(expense.vendor_name)
      ) {
        grouped[key].vendors.push(expense.vendor_name);
      }

      if (
        dayjs(expense.expense_date).isAfter(
          grouped[key].last_purchase
        )
      ) {
        grouped[key].last_purchase =
          expense.expense_date;
      }
    });
  });

  // =========================
  // FINAL ROWS
  // =========================
  return Object.values(grouped)

    .map((row, index) => {

      const qty = Number(row.total_qty || 0);

      const stockItem = ingredientStock.find(
        (i) =>
          i.name?.trim()?.toLowerCase() ===
          row.item_name?.trim()?.toLowerCase()
      );

      const remaining = Number(
        stockItem?.remaining_stock || 0
      );

      let stock_status = "ACTIVE";

      if (remaining <= 0) {
        stock_status = "INACTIVE";
      }

      return {
        ...row,
        key: `${row.item_name}-${index}`,
        remaining_stock: remaining,

        avg_rate:
          qty > 0
            ? Number(
                row.total_amount / qty
              ).toFixed(2)
            : 0,

        stock_status,
      };
    })

    .filter((row) => {

      // SEARCH FILTER
      const search =
        searchText.trim().toLowerCase();

      const matchSearch =
        !search ||
        [
          row.item_name,
          row.unit,
          row.stock_status,
          ...row.vendors,
        ]
          .join(" ")
          .toLowerCase()
          .includes(search);

      // STOCK FILTER
      const matchStock =
        stockFilter === "all" ||
        row.stock_status === stockFilter;

      return matchSearch && matchStock;
    });

}, [
  expenses,
  ingredientStock,
  searchText,
  stockFilter,
  dateFilter,
  range,
]);

  // ============================================
  // SUMMARY
  // ============================================
  const summary = useMemo(() => {
    return {
      totalItems: rows.length,
      totalQty: rows.reduce((sum, row) => sum + Number(row.total_qty || 0), 0),
      totalAmount: rows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0),
      lowStock: rows.filter((row) => row.stock_status === "INACTIVE").length,
    };
  }, [rows]);

  // ============================================
  // CLEAR FILTERS
  // ============================================
  const handleClear = () => {
    setSearchText("");
    setStockFilter("all");
    setDateFilter("all"); // CHANGED: from "today" to "all"
    setRange(null);
  };

  // ============================================
  // TABLE COLUMNS
  // ============================================
  const columns = [
    {
      title: "Item Name",
      dataIndex: "item_name",
      render: (value) => (
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{value || "-"}</div>
        </div>
      ),
    },
    {
      title: "Stock Qty",
      render: (_, row) => (
        <strong>
          {Number(row.total_qty || 0)} {row.unit}
        </strong>
      ),
    },
    {
        title: "Remaining Qty",
        render: (_, row) => {

          const remaining =
            Number(row.remaining_stock || 0);

          if (remaining <= 0) {

            return (
              <strong
                style={{
                  color: "red",
                  fontWeight: 700,
                }}
              >
                Out of Stock
              </strong>
            );
          }

          return (
            <strong
              style={{ color: "#1677ff" }}
            >
              {remaining} {row.unit}
            </strong>
          );
        },
      },
    {
      title: "Entries",
      dataIndex: "line_count",
      align: "center",
      render: (value) => <Tag color="blue">{Number(value || 0)}</Tag>,
    },
    {
      title: "Avg Rate",
      dataIndex: "avg_rate",
      align: "right",
      render: (value) => <span>₹{Number(value || 0).toFixed(2)}</span>,
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f172a" }}>₹{Number(value || 0).toFixed(2)}</strong>
      ),
    },
    {
      title: "Last Purchase",
      render: (_, row) => (row.last_purchase ? dayjs(row.last_purchase).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Vendors",
      render: (_, row) => (
        <Space wrap>
          {row.vendors?.map((vendor) => (
            <Tag key={vendor}>{vendor}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Stock Status",
      render: (_, row) => {
        let color = "green";
          let percent = 100;

          if (row.stock_status === "INACTIVE") {
            color = "red";
            percent = 0;
          }

        return (
          <div style={{ minWidth: 120 }}>
            <Tag color={color}>{row.stock_status}</Tag>
            <Progress
              percent={percent}
              size="small"
              status={row.stock_status === "INACTIVE" ? "exception" : "active"} 
            />
          </div>
        );
      },
    },
  ];

  // ============================================
  // UI
  // ============================================
  return (
    <div className="table-report-page">
      {/* HEADER */}
      <div className="table-report-hero">
        <div className="table-report-hero-left">
          <div className="table-report-hero-icon">
            <InboxOutlined />
          </div>
          <div>
            <h2 className="table-report-hero-title">Stock Report</h2>
            <p className="table-report-hero-subtitle">
              Restaurant stock overview from food stock purchases only
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
      <Button
        type="primary"
        icon={<FilePdfOutlined />}
        style={{
          background: "#d32029",
          borderColor: "#d32029",
          borderRadius: 8,
          fontWeight: 600,
          height: 42,
          paddingInline: 18,
        }}
        onClick={handlePDF}
      >
        PDF 1
      </Button>

      <Button
        type="default"
        icon={<FilePdfOutlined />}
        style={{
          borderRadius: 8,
          fontWeight: 600,
          height: 42,
        }}
        onClick={handleFullPDF}
      >
        PDF 2
      </Button>
    </div>
      </div>

      {/* MAIN CARD */}
      <Card className="table-report-card">
        {/* FILTERS */}
        <Row gutter={[12, 12]} className="table-report-filters">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select value={dateFilter} onChange={setDateFilter} style={{ width: "100%" }}>
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="all">All</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
          </Col>

          {dateFilter === "custom" && (
            <Col xs={24} sm={12} md={8} lg={6}>
             <RangePicker
            style={{ width: "100%" }}
            value={range}
            onChange={(vals) => setRange(vals)}
          />
            </Col>
          )}

          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              allowClear
              placeholder="Search stock item..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Select value={stockFilter} onChange={setStockFilter} style={{ width: "100%" }}>
              <Option value="all">All Stock</Option>
             <Option value="ACTIVE">Active</Option>
             <Option value="INACTIVE">Inactive</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              type="primary"
              block
              icon={<ReloadOutlined />}
              className="table-report-load-btn"
              onClick={() => fetchExpenses(true)}
              loading={loading}
            >
              Load
            </Button>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button block className="table-report-clear-btn" onClick={handleClear}>
              Clear
            </Button>
          </Col>
        </Row>

        {/* STATS */}
        <Row gutter={[16, 16]} className="table-report-stats">
          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic title="Items" value={summary.totalItems} prefix={<InboxOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic title="Total Qty" value={summary.totalQty} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Amount"
                value={summary.totalAmount}
                precision={2}
                formatter={(value) => `₹${Number(value || 0).toFixed(2)}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic title="Low Stock" value={summary.lowStock} prefix={<WarningOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* TABLE */}
        <Spin spinning={loading}>
          {!rows.length ? (
            <div className="table-report-empty">
              <Empty description="No stock items found. Add expenses with 'Add to Food Stock' checked." />
            </div>
          ) : (
            <div className="table-report-modern-table">
              <Table
                rowKey="key"
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 10 }}
                summary={(pageData) => {
                  const totalQty = pageData.reduce((sum, row) => sum + Number(row.total_qty || 0), 0);
                  const totalAmount = pageData.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
                  return (
                    <Table.Summary.Row style={{ background: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>
                        <b>Total</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <b>{totalQty}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>-</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>-</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <b style={{ color: "#0f172a" }}>₹{Number(totalAmount || 0).toFixed(2)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>-</Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>-</Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>-</Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default StockReportPage;